//React imports
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
//---

//CSS imports
import './TestTree.css'
//---

//PrimeReact imports
import { useForm, Controller } from 'react-hook-form';
import { Tree } from 'primereact/tree';
import { Toolbar } from 'primereact/toolbar';
import { Button } from 'primereact/button';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { ConfirmDialog } from 'primereact/confirmdialog';
//---

//Vendors imports
import axios from 'axios';
//---

//Components imports
import { useNotification } from '../NotificationProvider';
//---

//Utils imports
import { parseDashInPath } from '../../utils/Parser';
//---

//Data requests imports
import {
    deleteDbtTest,
    createDbtTest,
    checkDbtTestLock,
    closeDbtTest
} from '../../data/DbtTestData';
import {
    getAuthtokenClaim
} from '../../data/LoginData';
import {
    defaultTreeNode
} from '../../data/DefaultStates';
//---

const TestTree = ({ projectName, tests, listDbtTests, className }) => {
    const history = useHistory();

    const cancelTokenSource = axios.CancelToken.source();

    const { showNotification } = useNotification();

    const [selectedKey, setSelectedKey] = useState(null);
    const [selectedNode, setSelectedNode] = useState(defaultTreeNode);
    const [expandedKeys, setExpandedKeys] = useState({ 1: true });

    const defaultValues = {
        testName: ''
    }

    const { control, formState: { errors }, handleSubmit, reset } = useForm({ defaultValues });

    const [displayForm, setDisplayForm] = useState(false);

    const [confirmDialogState, setConfirmDialogState] = useState({
        visible: false,
        message: '',
        footer: null
    });

    const openDbtTestCtlr = (projectName, testName) => {
        checkDbtTestLock(cancelTokenSource, projectName, testName).then(
            data => {
                if (data.dbtFileSession) {
                    let sub = getAuthtokenClaim('sub')
                    if (sub) {
                        if (sub !== data.dbtFileSession['locked-by']) {
                            let message = <div>
                                The user <span style={{ fontWeight: 'bold' }}>{data.dbtFileSession['locked-by']}</span>
                                &nbsp;currently has a work session open on this test.<br />
                                You can <span style={{ fontWeight: 'bold' }}>continue</span> his session and take back his code
                                or <span style={{ fontWeight: 'bold' }}>overwrite</span> it and start again with a clean code.
                            </div>

                            setConfirmDialogState({
                                visible: true,
                                message: message,
                                footer: renderOpenConfirmDialogFooter(data.dbtFileSession['redis-id'])
                            });
                        } else {
                            goToTest('open');
                        }
                    } else {
                        showNotification('error', 'Error', 'invalid token, try to log in again or contact an administrator', 6000)
                        return;
                    }
                } else {
                    goToTest('open');
                }
            },
            errorMessage => showNotification('error', 'Error', errorMessage, 6000)
        );
    }

    const checkAndDeleteDbtTestCtlr = (testName) => {
        checkDbtTestLock(cancelTokenSource, projectName, testName).then(
            data => {
                if (data.dbtFileSession) {
                    let sub = getAuthtokenClaim('sub')
                    if (sub) {
                        if (sub !== data.dbtFileSession['locked-by']) {
                            let message = <div>
                                The user <span style={{ fontWeight: 'bold' }}>{data.dbtFileSession['locked-by']}</span>
                                &nbsp;currently has a work session open on this test.<br />
                                If you delete the test, his current work will be lost.
                            </div>

                            setConfirmDialogState({
                                visible: true,
                                message: message,
                                footer: renderDeleteConfirmDialogFooter
                            });
                        } else {
                            deleteDbtTestCtlr(testName);
                        }
                    } else {
                        showNotification('error', 'Error', 'invalid token, try to log in again or contact an administrator', 6000)
                        return;
                    }
                } else {
                    deleteDbtTestCtlr(testName);
                }
            },
            errorMessage => showNotification('error', 'Error', errorMessage, 6000)
        );
    }

    const closeAndOpenDbtTestCtlr = (dbtFileSessionRedisID) => {
        closeDbtTest(cancelTokenSource, projectName, dbtFileSessionRedisID).then(
            () => {
                goToTest('open')
            },
            errorMessage => showNotification('error', 'Error', errorMessage, 6000)
        );
    }

    const deleteDbtTestCtlr = (testName) => {
        deleteDbtTest(cancelTokenSource, projectName, testName).then(
            () => {
                listDbtTests()
                showNotification('success', 'Success', 'test successfully deleted', 6000)
            },
            errorMessage => showNotification('error', 'Error', errorMessage, 6000)
        );
    }

    const createDbtTestCtlr = (formData) => {
        if (selectedNode.type !== 'dir') {
            let message = <div>select a folder <i className="pi pi-folder" /> as location</div>
            showNotification('info', 'Info', message, 6000)
            return
        }

        let fullTestName = selectedNode.fullPath + formData.testName

        createDbtTest(cancelTokenSource, projectName, fullTestName).then(
            data => {
                listDbtTests()
                reset()
                showNotification('success', 'Success', 'test successfully created', 6000)
            },
            errorMessage => showNotification('error', 'Error', errorMessage, 6000)
        );
    }

    const onDelete = (event) => {
        let message = <div>
            Do you want to delete the <span style={{ fontWeight: 'bold' }}>{selectedNode.fullPath}</span> test ?
        </div>

        confirmPopup({
            target: event.currentTarget,
            message: message,
            icon: 'pi pi-info-circle',
            acceptClassName: 'p-button-danger',
            accept: () => checkAndDeleteDbtTestCtlr(selectedNode.fullPath),
        });
    }

    const goToTest = (mode) => {
        history.push('/' + projectName + "/test/" + mode + "/" + parseDashInPath(selectedNode.fullPath) + "/")
    }

    const getFormErrorMessage = (name) => {
        return errors[name] && <small className='p-error'>{errors[name].message}</small>
    };

    const testForm = () => {
        return (
            <div className='form-container p-mb-3'>
                <form onSubmit={handleSubmit(createDbtTestCtlr)} autoComplete='on'>
                    <div className='p-field'>
                        <Controller name='testName' control={control} rules={{ required: 'Name is required.' }} render={({ field, fieldState }) => (
                            <InputText
                                id={field.name}
                                {...field}
                                autoFocus
                                className={classNames('p-d-block test-name-input', { 'p-invalid': fieldState.invalid })}
                            />
                        )} />
                        <small id="testName-help" className="p-d-block">Enter the test name.</small>
                        {getFormErrorMessage('testName')}
                    </div>

                </form>
                <div className='buttons-container'>
                    <Button label="Cancel" icon="pi pi-times" onClick={() => { setDisplayForm(false); reset(); }} className="p-button-outlined p-mr-2" />
                    <Button label="Create" icon="pi pi-check" onClick={handleSubmit(createDbtTestCtlr)} />
                </div>
            </div>
        )
    }

    const renderOpenConfirmDialogFooter = (dbtFileSessionRedisID) => {
        return (
            <div>
                <Button label="Back" icon="pi pi-arrow-left" className="p-button-text"
                    onClick={() => setConfirmDialogState({ visible: false, message: '', footer: null })} />
                <Button label="Overwrite" icon="pi pi-times" className="p-button-danger"
                    onClick={() => closeAndOpenDbtTestCtlr(dbtFileSessionRedisID)} />
                <Button label="Continue" icon="pi pi-check"
                    onClick={() => goToTest('open')} />
            </div>
        );
    };

    const renderDeleteConfirmDialogFooter = () => {
        return (
            <div>
                <Button label="Back" icon="pi pi-arrow-left" className="p-button-text"
                    onClick={() => setConfirmDialogState({ visible: false, message: '', footer: null })} />
                <Button label="Delete" icon="pi pi-times" className="p-button-danger"
                    onClick={() => deleteDbtTestCtlr(selectedNode.fullPath)} />
            </div>
        );
    };

    const treeHeader = () => {
        const leftContents = (
            <React.Fragment>
                <Button
                    label="New"
                    icon="pi pi-plus"
                    disabled={selectedNode.type === 'dir' ? false : true}
                    onClick={() => setDisplayForm(true)}
                />
                <i className="pi pi-bars p-toolbar-separator p-mr-2" />
                <Button
                    label="Open"
                    icon="pi pi-external-link"
                    className="p-button p-mr-2"
                    disabled={selectedNode.type === 'file' ? false : true}
                    onClick={() => openDbtTestCtlr(projectName, selectedNode.fullPath)}
                />
                <Button
                    label="Show"
                    icon="pi pi-eye"
                    className="p-button p-mr-2"
                    disabled={selectedNode.type === 'file' ? false : true}
                    onClick={() => goToTest('read')}
                />
                <Button
                    label="Delete"
                    icon="pi pi-times"
                    className="p-button-outlined p-button-danger"
                    disabled={selectedNode.type === 'file' ? false : true}
                    onClick={(event) => onDelete(event)}
                />
            </React.Fragment>
        );

        return (
            <div>
                <Toolbar left={leftContents} />
                {
                    displayForm ?
                        testForm() :
                        null
                }
            </div>
        )
    }

    return (
        <div
            className={
                classNames('p-d-flex p-jc-center p-ai-center', displayForm ? 'small-test-tree' : 'test-tree', className)
            }
        >
            <ConfirmDialog
                header='Confirmation'
                message={confirmDialogState.message}
                icon='pi pi-exclamation-triangle'
                footer={confirmDialogState.footer}
                visible={confirmDialogState.visible}
                onHide={() => setConfirmDialogState({
                    visible: false,
                    message: '',
                    footer: null
                })}
            />
            <ConfirmPopup />
            <Tree
                value={tests}
                selectionMode='single'
                selectionKeys={selectedKey}
                onSelectionChange={e => setSelectedKey(e.value)}
                onSelect={e => setSelectedNode(e.node)}
                expandedKeys={expandedKeys}
                onToggle={e => setExpandedKeys(e.value)}
                header={treeHeader}
            />
        </div>
    );
};

export default TestTree;
